import {FormControl} from '@angular/forms';
import {ImportModuleGroupListKind} from '../../../core/definitions/import-module';
import {ObjectTypes} from '../../../core/definitions/object-types';
import {composeLineRowControls} from '../compose-field/compose-field.model';
import {ConceptOption} from '../concept-picker/concept-option.model';
import {lookupControlList} from '../lookup-controls';
import {
  applyValueSourceKind, createValueSourceControls, ValueSourceControls, ValueSourceKind, valueSourceMode,
} from '../value-source';

/**
 * The kinds of group a card serves: a history event
 * (`history_event_groups` + per-assignment `history_event_group`), with
 * Named-party and Place sub-blocks; a grouped inline-array item - Reference,
 * AlternativeNoItem, Measurement, Material, Title - (`<kind>_groups` +
 * per-assignment `group_id`), whose header selects and their
 * required-ness come from its `group_kinds` spec; and an admin event or a
 * placement (`admin_event_groups` + per-assignment `admin_event_group`),
 * whose member rows write onto a whole SECOND artifact context-linked back
 * to the row's own, not an inline array on it. One descriptor-driven
 * component renders every kind.
 */
export type GroupKind = ImportModuleGroupListKind | 'placement_event';

/** A bounded concept-list select in the card header (event type; or
 * reference type) or a sub-block header (named role, role status).
 * Options come from the same `/batch_fields` lists the generic batch card
 * uses. */
export interface GroupHeaderFieldDef {
  key: string;              // batch-field key, e.g. 'history_event_type_id'
  label: string;            // TRANS key
  required: boolean;
  options: ConceptOption[];
}

/** A member field: a column mapped into the group's inline-array item. */
export interface GroupMemberFieldDef {
  fieldId: string;          // prefixed MetaField id → the payload's target_field
  // The field's name on the group's item model (`alternative_number`,
  // `title`); unset where the target field carries none.
  fieldName?: string;
  label: string;
  // A concept-kind field (a Named-party or Place sub-block's member) -
  // "Fast verdi" is an Agent, NamedObject or Place pick, not free text.
  // Plain fields (dates, reference, url) take a plain input.
  concept: boolean;
  // This field may be added to a card more than once, each row becoming
  // its own separate array item (an admin event's `comments`) - unlike
  // every other member field, which has exactly one shared slot per group.
  repeatable?: boolean;
  // A free-text field, which several prefixed columns may be composed onto
  // (see `GroupMemberRow.composeControl`). Not a date or a concept.
  composable?: boolean;
  // The card writes nothing unless a member row onto this field sends a
  // value (a Tittel card's title, a Fast plassering's Location).
  required?: boolean;
}

/**
 * A repeatable sub-block of a group card. A history event's `named` and
 * `places` lists are each 1:M - `named` is one named party its own role /
 * status plus one `named_id` member; `place` is the structural twin, one
 * `place_id` member.
 */
export interface GroupSubBlockDef {
  kind: 'named' | 'place';
  titleKey: string;         // "Egennavn" / "Sted"
  addLabelKey: string;      // "Legg til egennavn" / "Legg til sted"
  header: GroupHeaderFieldDef[];   // [role_id, role_status_id]
  member: GroupMemberFieldDef;     // the named_id / place_id member
}

export interface GroupCardDescriptor {
  kind: GroupKind;
  titleKey: string;
  header: GroupHeaderFieldDef[];   // event_type_id | reference_type_id
  members: GroupMemberFieldDef[];  // event-level (from_date…) | reference + url
  subBlocks: GroupSubBlockDef[];   // [named, place] for history; [] for every other kind
  // The kind can create a term for a member value that matches nothing in
  // its concept list - its cards get the create-missing controls.
  supportsCreateMissing?: boolean;
  // Its cards get the only-when-mapped toggle (`onlyWhenMappedControl`).
  withOnlyWhenMappedToggle?: boolean;
  // Options for the "place new terms under" picker, shared by every card
  // of the kind.
  createMissingParentOptions?: ConceptOption[];
}

/** A member label without the leading `" / "` segment every member of
 * `descriptor` shares - the title of the item model the members sit on,
 * which the card itself stands for (`Historikkhendelser / Datering fra`
 * reads `Datering fra`). Unchanged on a card of fewer than two members,
 * or whose members share no such segment. */
export function memberLabelInCard(descriptor: GroupCardDescriptor, label: string): string {
  const leading = (text: string): string | null => {
    const end = text.indexOf(' / ');
    return end > 0 ? text.slice(0, end + ' / '.length) : null;
  };
  const shared = new Set(descriptor.members.map(member => leading(member.label)));
  const [prefix] = shared;
  if (descriptor.members.length < 2 || shared.size !== 1 || !prefix || !label.startsWith(prefix)) {
    return label;
  }
  return label.slice(prefix.length);
}

/** How a member row gets its value: from a source column (a `{Column}`
 * template) or a fixed value typed / picked by the operator. */
export type MemberSource = 'column' | 'constant';

/** For a concept member (a Named party / Place sub-block), what the value
 * (column or fixed) actually is: a name to look up / an Agent or Place id
 * the operator picked (`name`), or a KulturNav objectid to resolve/import
 * (`kulturnav_uuid`). Ignored for non-concept members. */
export type MemberValueType = 'name' | 'kulturnav_uuid';

/** What a Named sub-block's values resolve to, and what a name that
 * matches nothing creates: an Agent or a NamedObject. The wire
 * `object_type`; in KulturNav-UUID mode an Agent resolves against
 * kulturnav.org and a NamedObject against data.arkeologi.org. */
export type NamedObjectType = ObjectTypes.AGENT | ObjectTypes.NAMED_OBJECT;

/** The concept list a Named sub-block's `type_id` is a term of, per
 * object type: `agent_type_l` or `named_object_type_l`. */
export const NAMED_TYPE_CONCEPT_TYPES: Record<NamedObjectType, string> = {
  [ObjectTypes.AGENT]: 'ct_23',
  [ObjectTypes.NAMED_OBJECT]: 'ct_111',
};

/** The Person term of `agent_type_l`: an Agent sub-block's type until
 * another is picked, and what an Agent sub-block stored with no type
 * restores as. */
export const AGENT_TYPE_PERSON = 'ct_23-784b329e-5a5d-4db7-ab34-f316d0b54f34';
/** The Organisasjon term of `agent_type_l`: what an older stored
 * `agent_type: "organization"` restores as. */
export const AGENT_TYPE_ORGANIZATION = 'ct_23-fb8b920d-b709-4783-ab98-f6fcc1d0ea6f';

/** The type a Named sub-block of `objectType` starts with: Person for an
 * Agent, none for a NamedObject, whose type the operator must pick. */
export function defaultNamedTypeId(objectType: NamedObjectType): string | null {
  return objectType === ObjectTypes.AGENT ? AGENT_TYPE_PERSON : null;
}

/** A Named sub-block's target: the Objekttype pick and the type picker
 * after it. `typeIdControl` is a term of the object type's list
 * (`NAMED_TYPE_CONCEPT_TYPES`), the type a record created from a name
 * gets; it applies only while the sub-block takes names from a column
 * (`namedTakesNames`). A KulturNav uuid brings its record's own type, and
 * a fixed pick creates nothing. */
export interface NamedTargetControls {
  objectTypeControl: FormControl<NamedObjectType>;
  typeIdControl: FormControl<string | null>;
}

export function createNamedTargetControls(
  objectType: NamedObjectType = ObjectTypes.AGENT, typeId: string | null = defaultNamedTypeId(objectType),
): NamedTargetControls {
  return {
    objectTypeControl: new FormControl<NamedObjectType>(objectType, {nonNullable: true}),
    typeIdControl: new FormControl<string | null>(typeId),
  };
}

// The value-source controls (see `ValueSourceControls`) are the row's
// value for every "Hentes fra" choice but Fast verdi, which takes
// `constantControl` or `conceptControl` instead.
export interface GroupMemberRow extends ValueSourceControls {
  // Stable across a drag/rebuild, unlike `fieldId` - a repeatable field
  // (see `GroupMemberFieldDef.repeatable`) may have several rows sharing
  // one `fieldId`, so `id` (not `fieldId`) is what a template `track`s by.
  id: number;
  fieldId: string;
  sourceControl: FormControl<MemberSource>;
  valueTypeControl: FormControl<MemberValueType>;  // concept members only
  constantControl: FormControl<string>;        // "Fast verdi", plain field / a pasted KulturNav UUID
  // "Fast verdi", concept field → the Agent / NamedObject / Place artifact_id
  conceptControl: FormControl<string | null>;
}

/**
 * One instance of a repeatable sub-block. `key` is the payload sub-key a
 * member mapping row carries (`sub_block`); the client uses
 * `'<group>.<n>'`.
 */
export interface GroupSubBlockRow {
  key: string;
  defKind: 'named' | 'place';
  headerControls: { [key: string]: FormControl<string | null> };  // role_id, role_status_id
  member: GroupMemberRow;
  target?: NamedTargetControls;  // Named sub-blocks only
}

/**
 * The mutable state of one group. Owned by the page (so its controls'
 * subscriptions are registered on the page's subscription bag and its
 * values feed the run payload); the card renders and mutates it.
 */
export interface GroupCardModel {
  key: string;                                             // 'g0', 'g1' … stable
  headerControls: { [key: string]: FormControl<string | null> };
  members: GroupMemberRow[];
  subBlocks: GroupSubBlockRow[];                           // [] for every kind but history events
  // Admin-event groups only: when checked, this group's event
  // fires for a row only if one of its mapped member columns has content -
  // unset/unchecked keeps the default of firing on every row from the
  // header picks alone. `undefined` for every other kind, which has no
  // such toggle.
  onlyWhenMappedControl?: FormControl<boolean>;
  // Only for a kind whose descriptor `supportsCreateMissing`: whether a
  // member value matching nothing gets a new term created for it, and the
  // existing term a new one is placed under (`null` = a root term).
  createMissingControl?: FormControl<boolean>;
  createMissingParentControl?: FormControl<string | null>;
}

// Only ever read as a template `track` key (see `GroupMemberRow.id`'s doc
// comment) - a module-local counter is enough, no need for the page to own
// it the way it owns `key`s that end up in the wire payload.
let nextMemberRowId = 0;

export function createGroupMemberRow(
  fieldId: string, source: MemberSource = 'column'): GroupMemberRow {
  return {
    id: nextMemberRowId++,
    fieldId,
    sourceControl: new FormControl<MemberSource>(source, {nonNullable: true}),
    valueTypeControl: new FormControl<MemberValueType>('name', {nonNullable: true}),
    constantControl: new FormControl<string>('', {nonNullable: true}),
    conceptControl: new FormControl<string | null>(null),
    ...createValueSourceControls(),
  };
}

/** A member row's "Hentes fra" choice: Fast verdi while it holds a fixed
 * value, else the value-source mode its parts are read from. */
export function memberValueSourceKind(row: GroupMemberRow): ValueSourceKind {
  if (row.sourceControl.value === 'constant') {
    return 'constant';
  }
  const mode = valueSourceMode(row);
  return mode === 'template' ? 'column' : mode;
}

/** Switches a member row to `kind`: its source to a fixed value or a
 * column, and lookup and compose mode to `kind`'s (see
 * `applyValueSourceKind`). A control already set right is left alone. */
export function setMemberValueSourceKind(row: GroupMemberRow, kind: ValueSourceKind): void {
  const source: MemberSource = kind === 'constant' ? 'constant' : 'column';
  if (row.sourceControl.value !== source) {
    row.sourceControl.setValue(source);
  }
  applyValueSourceKind(row, kind);
}

export function createGroupCardModel(
  key: string, header: GroupHeaderFieldDef[], withOnlyWhenMappedToggle = false, withCreateMissing = false,
): GroupCardModel {
  const headerControls: { [key: string]: FormControl<string | null> } = {};
  for (const def of header) {
    headerControls[def.key] = new FormControl<string | null>(null);
  }
  const model: GroupCardModel = {key, headerControls, members: [], subBlocks: []};
  if (withOnlyWhenMappedToggle) {
    model.onlyWhenMappedControl = new FormControl<boolean>(false, {nonNullable: true});
  }
  if (withCreateMissing) {
    model.createMissingControl = new FormControl<boolean>(false, {nonNullable: true});
    model.createMissingParentControl = new FormControl<string | null>(null);
  }
  return model;
}

export function createGroupSubBlockRow(key: string, def: GroupSubBlockDef): GroupSubBlockRow {
  const headerControls: { [key: string]: FormControl<string | null> } = {};
  for (const headerDef of def.header) {
    headerControls[headerDef.key] = new FormControl<string | null>(null);
  }
  const row: GroupSubBlockRow = {
    key, defKind: def.kind, headerControls, member: createGroupMemberRow(def.member.fieldId)};
  if (def.kind === 'named') {
    row.target = createNamedTargetControls();
  }
  return row;
}

/** Whether `row` is a Named sub-block whose values are names read from a
 * column - the one case where a name that matches nothing creates a
 * record, and so the one case its type applies to. */
export function namedTakesNames(row: GroupSubBlockRow): boolean {
  return !!row.target && row.member.sourceControl.value === 'column'
    && row.member.valueTypeControl.value !== 'kulturnav_uuid';
}

/** Whether `row` is a Named sub-block of a NamedObject that takes names
 * (`namedTakesNames`) and still lacks the type a created NamedObject
 * needs. */
export function namedTypeMissing(row: GroupSubBlockRow): boolean {
  return namedTakesNames(row) && row.target!.objectTypeControl.value === ObjectTypes.NAMED_OBJECT
    && !row.target!.typeIdControl.value;
}

/** The `TRANS__` key of the title of a NamedObject sub-block's type
 * picker. */
export const NAMED_OBJECT_TYPE_LABEL_KEY = 'TRANS__IMPORT__GROUP_CARD__NAMED_OBJECT_TYPE';

/** Every control on a group model, for the page to register on its
 * subscription bag (values feed the run payload / preflight staleness). */
export function groupModelControls(model: GroupCardModel): FormControl[] {
  return [
    ...Object.values(model.headerControls),
    ...(model.onlyWhenMappedControl ? [model.onlyWhenMappedControl] : []),
    ...(model.createMissingControl ? [model.createMissingControl] : []),
    ...(model.createMissingParentControl ? [model.createMissingParentControl] : []),
    ...model.members.flatMap(memberRowControls),
    ...model.subBlocks.flatMap(subBlockRowControls),
  ];
}

export function memberRowControls(row: GroupMemberRow): FormControl[] {
  return [
    row.sourceControl, row.valueTypeControl,
    row.templateControl, row.constantControl, row.conceptControl,
    row.composeControl, ...row.composeRows.flatMap(composeLineRowControls),
    ...lookupControlList(row),
  ];
}

/** Every member row of a group model, sub-block members included. */
export function groupModelMemberRows(model: GroupCardModel): GroupMemberRow[] {
  return [...model.members, ...model.subBlocks.map(sub => sub.member)];
}

export function subBlockRowControls(row: GroupSubBlockRow): FormControl[] {
  return [
    ...Object.values(row.headerControls),
    ...(row.target ? [row.target.objectTypeControl, row.target.typeIdControl] : []),
    ...memberRowControls(row.member),
  ];
}
